import {IconButton} from '@dropbox/dig-components/buttons';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, DeleteLine} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import {resolveStampedEditorStateMapValue} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {PlanningCommentableRichText} from 'components/DSYS/editor/dynamicNode/PlanningCommentableRichText';
import {PlanningCommentField} from 'components/DSYS/editor/dynamicNode/PlanningCommentField';
import {
  isLexicalJsonString,
  parseAndExtractTextFromLexicalJson,
} from 'components/DSYS/editor/utils/lexical';
import {t} from 'i18next';
import type {LexicalEditor} from 'lexical';
import {type MouseEvent, type RefObject, useEffect, useRef, useState} from 'react';

import {
  createStrategyEntryId,
  createStrategyEntryStableKey,
  getStrategyEntryPlainText,
  mergeStrategyEntries,
  type StrategyEntry,
  type StrategyNodeType,
} from './strategyEntries';
import styles from './TypeaheadNode.module.css';

const getObstaclesAndOpportunitiesPlaceholder = () =>
  t('strategy_obstacles_and_opportunities_placeholder')?.toString?.() ??
  'ex. Previous attempts to drive multi-product adoption have not been successful';

const getGuidingPrinciplesPlaceholder = () =>
  t('strategy_guiding_principles_placeholder')?.toString?.() ??
  'ex. Modernize selectively where it amplifies strategic convergence — invest in FSS upgrades only where they directly support the Bridge vision and Dash AI capabilities';

/**
 * Rich-text list editor for strategy obstacles/opportunities and guiding principles.
 *
 * Data sources:
 * - `value`: Array of entries from DB (normalized in parent StrategyKernel)
 * - `editorStateMapJson`: Map of stable entry key → rich Lexical JSON with comment highlights from Hocuspocus
 *
 * Stable key format:
 * - Saved entries (positive ID): `${nodeType}:id:${entry.id}` - stable across reordering and deletions
 * - Pending entries (negative ID): `${nodeType}:pid:${index}:${contentHash}` - migrated to ID-based key when saved
 * - Comments persist even when the text is edited
 *
 * Rendering per entry:
 * 1. Look up `editorStateMapJson[stableKey]` (has comments, staleness-checked against plain text)
 * 2. Fall back to plain text if key not found
 *
 * Persistence:
 * - Plain text → DB via `onSave` (extracts and saves plain text only)
 * - Editor state → Local state via `onChange` (enables merge logic between local + DB)
 * - Rich Lexical JSON with comments → Hocuspocus via `onPersistKey`
 * - Key migration: When pending entry gets saved, comments migrate from pid-based to id-based key
 */
export const StrategyEntriesRichTextListEditor = ({
  editor,
  planningId,
  nodeType,
  value,
  editorStateMapJson,
  onPersistKey,
  onSave,
}: {
  editor: LexicalEditor;
  planningId: string | undefined;
  nodeType: StrategyNodeType;
  value: StrategyEntry[]; // DB plain text
  editorStateMapJson: string | undefined; // Map of stable entry key → rich Lexical JSON with comments
  onPersistKey: (
    entryKey: string,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void; // Persists rich state with comments to Hocuspocus
  onSave: (entry: StrategyEntry, action: 'create' | 'update' | 'delete') => Promise<void>; // Saves individual entry to DB
}) => {
  const placeholder =
    nodeType === 'guiding-principles'
      ? getGuidingPrinciplesPlaceholder()
      : getObstaclesAndOpportunitiesPlaceholder();
  const labelPrefix = nodeType === 'guiding-principles' ? 'GP' : 'O';

  const [currentEntries, setCurrentEntries] = useState<StrategyEntry[]>(value);
  const [pendingEntryId, setPendingEntryId] = useState<number | null>(null);
  const [autoFocusEntryId, setAutoFocusEntryId] = useState<number | null>(null);
  const addButtonContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Whenever entries change in DB (savedValue), merge the changes with current local state.
    // This preserves comments on entries whose plain text hasn't changed in DB,
    // while still updating entries that were modified elsewhere
    setCurrentEntries((prev) => {
      const merged = mergeStrategyEntries(prev, value);

      // Skip update if merge result is identical to current state.
      // This prevents unnecessary re-renders during refetches
      if (JSON.stringify(merged) === JSON.stringify(prev)) {
        return prev;
      }

      return merged;
    });
  }, [value, nodeType]);

  if (!editor.isEditable() && value.length === 0) {
    return null;
  }

  if (!editor.isEditable()) {
    return (
      <Box className={styles.obstaclesAndOpportunitiesContainer}>
        {value.map((entry, index) => (
          <StrategyEntryRow
            key={entry.id}
            planningId={planningId}
            index={index}
            labelPrefix={labelPrefix}
            entry={entry}
            editable={false}
            placeholder={placeholder}
            nodeType={nodeType}
            editorStateMapJson={editorStateMapJson}
            onPersistKey={onPersistKey}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box className={styles.obstaclesAndOpportunitiesContainer}>
      {currentEntries.map((entry, index) => {
        const isPending = pendingEntryId === entry.id;
        const shouldAutoFocus = autoFocusEntryId === entry.id;

        return (
          <StrategyEntryRow
            key={entry.id}
            planningId={planningId}
            index={index}
            labelPrefix={labelPrefix}
            entry={entry}
            editable={true}
            autoFocus={shouldAutoFocus}
            isPending={isPending}
            addButtonContainerRef={addButtonContainerRef}
            nodeType={nodeType}
            editorStateMapJson={editorStateMapJson}
            onPersistKey={onPersistKey}
            onChange={(nextEditorState) => {
              setCurrentEntries((prev) =>
                prev.map((currentEntry) =>
                  currentEntry.id === entry.id
                    ? {...currentEntry, editor_state: nextEditorState}
                    : currentEntry
                )
              );
            }}
            onDelete={() => {
              // Clean up the entry's keys from editorStateMapJson
              const stableKey = createStrategyEntryStableKey(entry, nodeType, index);
              onPersistKey(stableKey, undefined, undefined);
              onPersistKey(`${stableKey}:entryLabel`, undefined, undefined);

              if (isPending) {
                setPendingEntryId(null);
                setCurrentEntries((prev) => prev.filter((e) => e.id !== entry.id));
              } else {
                const newEntries = currentEntries.filter((e) => e.id !== entry.id);
                setCurrentEntries(newEntries);
                onSave(entry, 'delete');
              }
            }}
            onBlur={async (latestEditorState) => {
              const nextEntry = {...entry, editor_state: latestEditorState};
              const text = getStrategyEntryPlainText(nextEntry);

              // Clear autoFocus after the entry is blurred
              if (shouldAutoFocus) {
                setAutoFocusEntryId(null);
              }

              if (!text.trim()) {
                if (isPending) {
                  setPendingEntryId(null);
                  setCurrentEntries((prev) => prev.filter((e) => e.id !== entry.id));
                } else {
                  setCurrentEntries((prev) => prev.filter((e) => e.id !== entry.id));
                  await onSave(entry, 'delete');
                }
                return; // Don't save empty entries
              }

              if (isPending) {
                await onSave(nextEntry, 'create');
                setPendingEntryId(null);
              } else {
                await onSave(nextEntry, 'update');
              }
            }}
          />
        );
      })}
      {editor.isEditable() && (
        <Box
          ref={addButtonContainerRef}
          marginTop={currentEntries.length > 0 ? '4' : '0'}
          style={{height: '40px'}}
        >
          <IconButton
            variant="outline"
            size="small"
            shape="circular"
            onClick={async () => {
              const pendingEntry = currentEntries.find((e) => e.id === pendingEntryId);

              // Add new pending entry row before saving.
              // This ensures no flickering when DB refetch happens
              const newEntry = {id: createStrategyEntryId(), editor_state: ''};
              setPendingEntryId(newEntry.id);
              setAutoFocusEntryId(newEntry.id);
              setCurrentEntries((prev) => {
                // If the old pending entry is empty, filter it out
                if (pendingEntry) {
                  const text = getStrategyEntryPlainText(pendingEntry);
                  if (!text.trim()) {
                    // Clean up the empty pending entry's keys from editorStateMapJson
                    const entryIndex = prev.findIndex((e) => e.id === pendingEntry.id);
                    if (entryIndex >= 0) {
                      const stableKey = createStrategyEntryStableKey(
                        pendingEntry,
                        nodeType,
                        entryIndex
                      );
                      onPersistKey(stableKey, undefined, undefined);
                      onPersistKey(`${stableKey}:entryLabel`, undefined, undefined);
                    }
                    return [...prev.filter((e) => e.id !== pendingEntry.id), newEntry];
                  }
                }
                return [...prev, newEntry];
              });

              // Auto-save the previous pending entry if it has content
              if (pendingEntry) {
                const text = getStrategyEntryPlainText(pendingEntry);
                if (text.trim()) {
                  await onSave(pendingEntry, 'create');
                }
              }
            }}
            aria-label="icon button"
          >
            <UIIcon src={AddLine} size="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const StrategyEntryRow = ({
  planningId,
  index,
  labelPrefix,
  entry,
  editable,
  autoFocus,
  isPending,
  addButtonContainerRef,
  placeholder,
  nodeType,
  editorStateMapJson,
  onPersistKey,
  onChange,
  onDelete,
  onBlur,
}: {
  planningId: string | undefined;
  index: number;
  labelPrefix: string;
  entry: StrategyEntry;
  editable: boolean;
  autoFocus?: boolean;
  isPending?: boolean;
  addButtonContainerRef?: RefObject<HTMLDivElement | null>;
  placeholder?: string;
  nodeType: StrategyNodeType;
  editorStateMapJson?: string;
  onPersistKey?: (
    entryKey: string,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  onChange?: (nextEditorState: string) => void;
  onDelete?: () => void;
  onBlur?: (latestEditorState: string) => Promise<void> | void;
}) => {
  const textContent = getStrategyEntryPlainText(entry);
  const currentStableKey = createStrategyEntryStableKey(entry, nodeType, index);

  // Track the stable key across renders to detect ID transitions (unsaved → saved)
  const stableKeyRef = useRef(currentStableKey);
  const prevEntryIdRef = useRef(entry.id);

  // When a pending entry (negative ID) gets saved and receives a positive ID,
  // migrate comments from the old key (pid:index:hash) to the new key (id:dbId)
  useEffect(() => {
    if (entry.id !== prevEntryIdRef.current && entry.id > 0 && prevEntryIdRef.current < 0) {
      const oldKey = stableKeyRef.current; // idx:index:hash format
      const newKey = currentStableKey; // id:dbId format

      // Migrate comments from old key to new key
      if (oldKey !== newKey && onPersistKey && editorStateMapJson) {
        try {
          const editorStateMap = JSON.parse(editorStateMapJson) as Record<string, string>;
          const existingState = editorStateMap[oldKey];

          if (existingState) {
            // Persist under new key
            const plainText = parseAndExtractTextFromLexicalJson(existingState).trim();
            onPersistKey(newKey, existingState, plainText);

            // Clear old key
            onPersistKey(oldKey, undefined, undefined);
          }
        } catch {
          // Ignore malformed JSON
        }
      }

      stableKeyRef.current = newKey;
      prevEntryIdRef.current = entry.id;
    } else if (entry.id !== prevEntryIdRef.current) {
      // Different entry altogether - reset tracking
      stableKeyRef.current = currentStableKey;
      prevEntryIdRef.current = entry.id;
    }
  }, [entry.id, currentStableKey, onPersistKey, editorStateMapJson]);

  const stableKey = stableKeyRef.current;
  const isEmptyPending = Boolean(isPending && !textContent.trim());

  // Get editor state with comments from map, or plain text if not found.
  const fallbackEditorState = isLexicalJsonString(entry.editor_state)
    ? entry.editor_state
    : textContent;
  const editorStateValue = resolveStampedEditorStateMapValue(
    editorStateMapJson,
    stableKey,
    fallbackEditorState
  );
  const [localEditorState, setLocalEditorState] = useState(editorStateValue);
  const localEditorStateRef = useRef(editorStateValue);
  const [isRowFocused, setIsRowFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const isInsidePendingRowRef = useRef(false);
  const prevEditorStateValueRef = useRef(editorStateValue);
  const isEditing = isRowFocused || isSaving;

  // Pending rows save on blur, but double-clicks inside the row and add-button clicks can also blur.
  // Track where the mousedown started so only outside clicks save on blur
  useEffect(() => {
    const handleDocumentMouseDown = (event: globalThis.MouseEvent) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Node)) {
        isInsidePendingRowRef.current = false;
        return;
      }

      isInsidePendingRowRef.current =
        Boolean(rowRef.current?.contains(eventTarget)) ||
        Boolean(isPending && addButtonContainerRef?.current?.contains(eventTarget));
    };

    document.addEventListener('mousedown', handleDocumentMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
    };
  }, [addButtonContainerRef, isPending]);

  useEffect(() => {
    if (editorStateValue === prevEditorStateValueRef.current) {
      return;
    }

    prevEditorStateValueRef.current = editorStateValue;
    if (isEditing) {
      return;
    }

    localEditorStateRef.current = editorStateValue;
    setLocalEditorState(editorStateValue);
  }, [editorStateValue, isEditing]);

  const handleRowBlur = async () => {
    if (!onBlur) {
      return;
    }

    setIsSaving(true);
    try {
      await onBlur(localEditorStateRef.current);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      ref={rowRef}
      className={styles.obstaclesAndOpportunitiesRow}
      onBlur={(event) => {
        const relatedTarget = event.relatedTarget;
        if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
          return;
        }
        if (isInsidePendingRowRef.current) {
          return;
        }
        void handleRowBlur();
      }}
    >
      {planningId && onPersistKey ? (
        <Box className={styles.obstaclesAndOpportunitiesLabelField}>
          <PlanningCommentField
            mapKey={`${stableKey}:entryLabel`}
            mapJson={editorStateMapJson}
            fallbackText={`${labelPrefix}${index + 1}:`}
            planningId={planningId}
            onPersistKey={onPersistKey}
            not100
            selectionControls={['styling', 'highlighting', 'comments']}
            fallback={(text) => (
              <span className={styles.obstaclesAndOpportunitiesLabel}>{text}</span>
            )}
          />
        </Box>
      ) : (
        <span
          className={styles.obstaclesAndOpportunitiesLabel}
        >{`${labelPrefix}${index + 1}:`}</span>
      )}
      <Box
        className={cx(styles.obstaclesAndOpportunitiesContent, {
          [styles.obstaclesAndOpportunitiesEmptyPendingContent]: isEmptyPending,
        })}
      >
        {editable || textContent ? (
          <Box className={styles.obstaclesAndOpportunitiesRichTextArea}>
            <PlanningCommentableRichText
              planningId={planningId}
              value={localEditorState}
              editable={editable}
              autoFocus={autoFocus ?? false}
              selectionControls={['styling', 'highlighting', 'comments']}
              hideToolbar
              disableBorder
              minHeight={0}
              editablePaddingX="0"
              editablePaddingY="0"
              squareBorders
              placeholder={placeholder}
              onToggleFocused={setIsRowFocused}
              onChange={(state) => {
                const stateString = state.toString();
                localEditorStateRef.current = stateString;
                setLocalEditorState(stateString);

                // Extract plain text from the editor state for staleness checking
                const plainText = parseAndExtractTextFromLexicalJson(stateString).trim();

                // Persist rich editor state with comments to Hocuspocus.
                // For saved entries, the stable key uses the database ID, so comments
                // persist even when text is edited. For pending entries, the key includes
                // content hash but is locked during the editing session to prevent focus loss.
                onPersistKey?.(stableKey, stateString, plainText);

                // Update plain text in local state and Lexical node.
                // This maintains the array structure needed for DB sync and merge logic
                onChange?.(stateString);
              }}
            />
          </Box>
        ) : (
          <span className={styles.obstaclesAndOpportunitiesReadOnlyText}>{placeholder}</span>
        )}
        {editable && onDelete ? (
          <IconButton
            variant="transparent"
            size="small"
            shape="circular"
            className={styles.obstaclesAndOpportunitiesButton}
            onMouseDown={(event: MouseEvent) => {
              event.preventDefault();
            }}
            onClick={() => onDelete()}
            aria-label="icon button"
          >
            <UIIcon src={DeleteLine} size="small" />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
};
